// Utility functions to transform App-v2 data structures to backend API format

interface BillData {
  customerName: string;
  address: string;
  postcode?: string;
  annualUsage: number;
  monthlyBill: number;
  supplier: string;
  standingCharge?: number;
  monthlySpend?: number;
  yearlySpend?: number;
  usagePattern?: {
    daily: Array<{ hour: number; usage: number }>;
  };
}

interface PropertyData {
  roofArea: number;
  roofOrientation: string;
  shadingFactor: number;
  solarPotential: string;
}

interface SystemConfig {
  solar: {
    size: number;
    panels: number;
    cost: number;
    generation: number;
    baseGeneration?: number;
    orientationShadingAdjustment?: number;
    projectedGeneration?: number;
  };
  battery: {
    size: number;
    cost: number;
  };
  total: {
    cost: number;
    savings: number;
  };
  breakdown: {
    billSavings: number;
    exportIncome: number;
    batteryArbitrage: number;
  };
}

interface FinanceConfig {
  deposit: number;
  term: number;
  monthlyPayment: number;
  netMonthlyCost: number;
}

interface BackendQuotePayload {
  // Customer Information
  customer_name: string;
  address: string;
  meter_number: string;
  annual_consumption_kwh: number;
  current_supplier: string;
  current_tariff: string;
  monthly_bill: number;
  upload_date: string;
  
  // Solar Assessment Data
  roof_orientation: string;
  roof_pitch: string;
  shading_level: string;
  roof_condition: string;
  installation_preference: string;
  
  // Quote Data
  system_capacity_kw: number;
  annual_production: number;
  estimated_cost: number;
  payback_period: number;
  carbon_savings: number;
  system_size: number;
  system_cost: number;
  monthly_finance: number;
  monthly_savings: number;
  net_monthly_saving: number;
  break_even_years: number;
  generated_at: string;
  // Generation breakdown (orientation & shading from Google Solar)
  base_annual_generation_kwh?: number;
  orientation_shading_adjustment_kwh?: number;
  projected_annual_generation_kwh?: number;
}

/** Serialize for multipart quote POST; avoids NaN/Infinity strings. */
export function formatAnnualProductionForFormData(value: unknown): string {
  const n = Number(value);
  return String(Number.isFinite(n) ? Math.round(n) : 0);
}

export function formatCarbonSavingsForFormData(value: unknown): string {
  const n = Number(value);
  return String(Number.isFinite(n) ? Math.round(n * 100) / 100 : 0);
}

/**
 * Extract postcode from UK address string
 */
export function extractPostcode(address: string): string {
  // UK postcode pattern: letters, numbers, space, letters/numbers
  const postcodeRegex = /([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})/i;
  const match = address.match(postcodeRegex);
  return match ? match[1].trim() : '';
}

export type UkAddressParts = {
  address1: string;
  address2: string;
  city: string;
  region: string;
  postcode: string;
  udprn?: string;
  uprn?: string;
  latitude?: number;
  longitude?: number;
};

/** Collapse ", , …" from OCR/extraction into a single comma (common before postcodes). */
function normalizeAddressCommaRuns(s: string): string {
  return s
    .replace(/,\s*(?:,\s*)+/g, ', ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Split a UK address string into form fields.
 * Handles OCR quirks like a lone house number before the street ("6, NORWICH DRIVE").
 */
export function parseUkAddressParts(raw: string): UkAddressParts {
  const empty: UkAddressParts = {
    address1: '',
    address2: '',
    city: '',
    region: '',
    postcode: '',
  };
  const normalized = normalizeAddressCommaRuns((raw || '').trim());
  if (!normalized) return empty;

  const pcMatch = normalized.match(/([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})/i);
  const postcode = pcMatch ? pcMatch[1].trim().toUpperCase() : '';
  const withoutPc = normalizeAddressCommaRuns(
    pcMatch
      ? `${normalized.slice(0, pcMatch.index)}${normalized.slice((pcMatch.index ?? 0) + pcMatch[0].length)}`
      : normalized,
  );

  let parts = withoutPc
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  // Merge leading numeric-only segment with the next (e.g. "6" + "NORWICH DRIVE")
  if (parts.length >= 2 && /^\d+[A-Za-z]?$/.test(parts[0])) {
    parts = [`${parts[0]} ${parts[1]}`, ...parts.slice(2)];
  }

  if (parts.length === 0) {
    return { ...empty, postcode };
  }
  if (parts.length === 1) {
    return { ...empty, address1: parts[0], postcode };
  }
  if (parts.length === 2) {
    return { ...empty, address1: parts[0], city: parts[1], postcode };
  }
  if (parts.length === 3) {
    return {
      ...empty,
      address1: parts[0],
      city: parts[1],
      region: parts[2],
      postcode,
    };
  }

  // 4+: address1, address2 (middle joined), city, region
  return {
    address1: parts[0],
    address2: parts.slice(1, -2).join(', '),
    city: parts[parts.length - 2],
    region: parts[parts.length - 1],
    postcode,
  };
}

/** Compose a single address line from split fields (postcode excluded). */
export function composeAddress(parts: {
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
}): string {
  return [parts.address1, parts.address2, parts.city, parts.region]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(', ');
}

/** Build confirm-address payload from Analysis step form fields. */
export function formToUkAddressParts(fields: {
  address1: string;
  address2?: string;
  city?: string;
  region?: string;
  postcode: string;
  uprn?: string;
  udprn?: string;
}): UkAddressParts | null {
  const address1 = fields.address1.trim();
  const postcode = fields.postcode.trim();
  if (!address1 || !postcode) {
    return null;
  }

  return {
    address1,
    address2: (fields.address2 ?? '').trim(),
    city: (fields.city ?? '').trim(),
    region: (fields.region ?? '').trim(),
    postcode: postcode.toUpperCase(),
    ...(fields.uprn?.trim() ? { uprn: fields.uprn.trim() } : {}),
    ...(fields.udprn?.trim() ? { udprn: fields.udprn.trim() } : {}),
  };
}

/** Map Ideal Postcodes lookup result to quote form address fields. */
export function addressLookupToFormFields(a: {
  line1: string;
  line2?: string;
  line3?: string;
  city: string;
  region?: string;
  postcode: string;
  udprn?: string;
  uprn?: string;
  latitude?: number;
  longitude?: number;
}): UkAddressParts {
  const address2Parts = [a.line2, a.line3].map((p) => (p || '').trim()).filter(Boolean);
  return {
    address1: a.line1.trim(),
    address2: address2Parts.join(', '),
    city: a.city.trim(),
    region: (a.region || '').trim(),
    postcode: a.postcode.trim().toUpperCase(),
    udprn: a.udprn,
    uprn: a.uprn,
    ...(typeof a.latitude === 'number' && Number.isFinite(a.latitude) ? { latitude: a.latitude } : {}),
    ...(typeof a.longitude === 'number' && Number.isFinite(a.longitude) ? { longitude: a.longitude } : {}),
  };
}

/**
 * Build one display line from bill fields; appends postcode when missing from the line
 * and avoids duplicating the postcode if it is already present.
 */
export function formatExtractedBillAddress(
  d: { fullAddress?: string; address?: string; city?: string; postcode?: string },
  fallback = '42 Maple Grove, Manchester, M20 4BF'
): string {
  const full = normalizeAddressCommaRuns((d.fullAddress || '').trim());
  const addr = normalizeAddressCommaRuns((d.address || '').trim());
  const city = (d.city || '').trim();
  const pc = (d.postcode || '').trim();

  const norm = (s: string) => s.replace(/\s/g, '').toUpperCase();
  const textHasPostcode = (text: string) => !pc || norm(text).includes(norm(pc));

  const primary = full || addr;

  if (!primary && !city && !pc) {
    return fallback;
  }

  const parts: string[] = [];

  if (primary) {
    parts.push(primary);
    if (city && !primary.toLowerCase().includes(city.toLowerCase())) {
      parts.push(city);
    }
    const joined = parts.join(', ');
    if (pc && !textHasPostcode(joined)) {
      parts.push(pc);
    }
  } else {
    if (city) {
      parts.push(city);
    }
    const joined = parts.join(', ');
    if (pc && !textHasPostcode(joined)) {
      parts.push(pc);
    }
  }

  return normalizeAddressCommaRuns(parts.join(', ')) || fallback;
}

/**
 * Map roof orientation from App-v2 format to backend format
 */
function mapRoofOrientation(orientation: string): string {
  const mapping: Record<string, string> = {
    'South-facing': 'South',
    'South-East': 'South-East',
    'South-West': 'South-West',
    'East': 'East',
    'West': 'West',
    'North': 'North',
  };
  return mapping[orientation] || 'South';
}

/**
 * Map shading factor to shading level description
 */
function mapShadingLevel(shadingFactor: number): string {
  if (shadingFactor >= 0.9) {
    return 'None - Full sun all day';
  } else if (shadingFactor >= 0.75) {
    return 'Minimal - Occasional shadows';
  } else if (shadingFactor >= 0.5) {
    return 'Moderate - Some shading';
  } else {
    return 'Heavy - Significant shading';
  }
}

/**
 * Map bill data to backend format
 */
export function mapBillDataToBackend(billData: BillData, uploadDate?: string): Partial<BackendQuotePayload> {
  return {
    customer_name: billData.customerName,
    address: billData.address,
    annual_consumption_kwh: billData.annualUsage,
    current_supplier: billData.supplier,
    monthly_bill: billData.monthlyBill,
    upload_date: uploadDate || new Date().toISOString(),
    // Defaults for missing fields
    meter_number: 'Not found', // Will be extracted from bill if available
    current_tariff: 'Standard Variable', // Will be extracted from bill if available
  };
}

/**
 * Map property data to backend format
 */
export function mapPropertyDataToBackend(propertyData: PropertyData): Partial<BackendQuotePayload> {
  return {
    roof_orientation: mapRoofOrientation(propertyData.roofOrientation),
    roof_pitch: 'Medium (25-35°)', // Default, can be enhanced later
    shading_level: mapShadingLevel(propertyData.shadingFactor),
    roof_condition: 'Good - In good condition', // Default, can be enhanced later
    installation_preference: 'Standard installation', // Default
  };
}

/**
 * Map system config to backend format
 */
export function mapSystemConfigToBackend(systemConfig: SystemConfig): Partial<BackendQuotePayload> {
  const paybackPeriod = systemConfig.total.cost / systemConfig.total.savings;
  const gen = Number(systemConfig.solar.generation);
  const carbonRaw = Number.isFinite(gen) ? gen * 0.0004 : 0; // tonnes CO2 per kWh
  const projectedRaw = systemConfig.solar.projectedGeneration ?? systemConfig.solar.generation;
  const projected = Number(projectedRaw);
  const annualProduction = Number.isFinite(projected) ? Math.round(projected) : 0;
  const carbonSavings = Number.isFinite(carbonRaw) ? Math.round(carbonRaw * 10) / 10 : 0;

  const payload: Partial<BackendQuotePayload> = {
    system_capacity_kw: systemConfig.solar.size,
    system_size: systemConfig.solar.size,
    annual_production: annualProduction,
    estimated_cost: systemConfig.total.cost,
    system_cost: systemConfig.total.cost,
    monthly_savings: Math.round(systemConfig.total.savings / 12),
    payback_period: Math.round(paybackPeriod * 10) / 10, // Round to 1 decimal
    carbon_savings: carbonSavings,
    break_even_years: Math.round(paybackPeriod),
  };

  if (systemConfig.solar.baseGeneration !== undefined) {
    payload.base_annual_generation_kwh = Math.round(systemConfig.solar.baseGeneration);
  }
  if (systemConfig.solar.orientationShadingAdjustment !== undefined) {
    payload.orientation_shading_adjustment_kwh = systemConfig.solar.orientationShadingAdjustment;
  }
  if (systemConfig.solar.projectedGeneration !== undefined) {
    payload.projected_annual_generation_kwh = Math.round(systemConfig.solar.projectedGeneration);
  }

  return payload;
}

/**
 * Map finance config to backend format
 */
export function mapFinanceConfigToBackend(financeConfig: FinanceConfig): Partial<BackendQuotePayload> {
  return {
    monthly_finance: financeConfig.monthlyPayment,
    net_monthly_saving: financeConfig.netMonthlyCost,
  };
}

/**
 * Combine all data into complete backend quote payload
 */
export function combineToQuotePayload(
  billData: BillData,
  propertyData: PropertyData,
  systemConfig: SystemConfig,
  financeConfig: FinanceConfig,
  uploadDate?: string
): BackendQuotePayload {
  const bill = mapBillDataToBackend(billData, uploadDate);
  const property = mapPropertyDataToBackend(propertyData);
  const system = mapSystemConfigToBackend(systemConfig);
  const finance = mapFinanceConfigToBackend(financeConfig);

  return {
    ...bill,
    ...property,
    ...system,
    ...finance,
    generated_at: uploadDate || new Date().toISOString(),
  } as BackendQuotePayload;
}

/**
 * Map backend response to App-v2 bill data format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapBackendToBillData(backendData: any): BillData {
  // Generate usage pattern from annual usage
  const dailyUsage = backendData.annual_consumption_kwh / 365;
  const usagePattern = {
    daily: Array.from({ length: 24 }, (_, hour) => ({
      hour,
      usage: hour >= 16 && hour <= 20 
        ? dailyUsage * 1.2 / 24 
        : hour >= 7 && hour <= 9 
        ? dailyUsage * 0.9 / 24 
        : dailyUsage * 0.4 / 24
    }))
  };

  return {
    customerName: backendData.customer_name || 'Unknown',
    address: backendData.address || 'Address not found',
    postcode: extractPostcode(backendData.address || ''),
    annualUsage: backendData.annual_consumption_kwh || 0,
    monthlyBill: backendData.monthly_bill || 0,
    supplier: backendData.current_supplier || 'Unknown',
    standingCharge: backendData.standing_charge || 0,
    monthlySpend: backendData.monthly_spend || 0,
    yearlySpend: backendData.yearly_spend || 0,
    usagePattern,
  };
}

